import { stripSimpleMarkdown } from '../utils/simpleMarkdown';
import { t } from '../i18n';

const STORAGE_KEY = 'infinity-memoburst-card-messages';
const MAX_OPTION_COUNT = 20;
const MAX_SELECTED_COUNT = 2;

const EXCLUDED_WORDS = new Set(['the', 'a', 'an', 'i', 'you', 'he', 'she', 'it', 'they']);

export const CARD_MESSAGE_OPTIONS = [
  '시제',
  '의역이다',
  '복잡해',
  '쉽게생각',
  '길다',
  '앞',
  '뒤',
  '속지마',
  '아니다',
];

// Keep stored values stable across language switches; translate only at render.
// Retired options remain readable on cards that already saved them.
const PRESET_MESSAGE_VALUES = new Set([
  ...CARD_MESSAGE_OPTIONS, '시제일치', '단수복수', '생소하다', '짧다', '앞부분', '중간부분', '뒷부분', '조심해',
]);

export function getCardMessageLabel(message, language) {
  return PRESET_MESSAGE_VALUES.has(message)
    ? t(`practiceUi.userHints.${message}`, language)
    : message;
}

function normalizeOption(value) {
  return String(value || '').trim().slice(0, 60);
}

function getAnswerWords(card) {
  const answer = stripSimpleMarkdown(String(card?.back || ''));
  const words = answer.match(/[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/gu) || [];

  return words
    .map(normalizeOption)
    .filter(Boolean)
    .filter((word) => !EXCLUDED_WORDS.has(word.toLocaleLowerCase('en-US')));
}

export function getCardMessageOptions(card) {
  return Array.from(new Set([
    ...getAnswerWords(card),
    ...CARD_MESSAGE_OPTIONS,
  ])).slice(0, MAX_OPTION_COUNT);
}

function getStorageKey(userId, cardId) {
  return `${String(userId || 'anonymous')}::${String(cardId || '')}`;
}

function readAll() {
  if (typeof localStorage === 'undefined') return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(data) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data || {}));
}

function normalizeMessages(messages = []) {
  return Array.from(new Set(
    (Array.isArray(messages) ? messages : [])
      .map(normalizeOption)
      .filter(Boolean)
  )).slice(0, MAX_SELECTED_COUNT);
}

function getMessages({ userId, cardId } = {}) {
  if (!cardId) return [];
  const stored = readAll()[getStorageKey(userId, cardId)];
  return normalizeMessages(stored?.messages);
}

function saveMessages({ userId, cardId, messages } = {}) {
  if (!cardId) return;
  const normalized = normalizeMessages(messages);
  const all = readAll();
  const key = getStorageKey(userId, cardId);

  if (normalized.length === 0) {
    delete all[key];
  } else {
    all[key] = {
      messages: normalized,
      updatedAt: new Date().toISOString(),
    };
  }

  writeAll(all);
}

function hasMessages({ userId, cardId } = {}) {
  return getMessages({ userId, cardId }).length > 0;
}

const CardMessageService = {
  getCardMessageOptions,
  getMessages,
  saveMessages,
  hasMessages,
};

export default CardMessageService;
